import { ChevronDown, ChevronRight } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { InlineText, type InlineTextType } from './InlineText'

const STORAGE_PREFIX = 'vonk:sectie:'

function readOpen(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(`${STORAGE_PREFIX}${key}`)
    return value === null ? fallback : value === '1'
  } catch {
    return fallback
  }
}

function writeOpen(key: string, open: boolean) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, open ? '1' : '0')
  } catch {
    // Without storage the section simply opens with its default next time.
  }
}

/**
 * A collapsible group of properties in the left column (design §2.3, DS-16); the
 * open/closed state is remembered per user (localStorage).
 */
export function PropertySection({
  title,
  storageKey,
  defaultOpen = true,
  children,
}: {
  title: string
  storageKey: string
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(() => readOpen(storageKey, defaultOpen))
  const id = useId()
  return (
    <section className="flex flex-col gap-2 border-t border-border pt-3">
      <h2 className="text-sm font-semibold">
        <button
          type="button"
          aria-expanded={open}
          // The list is not rendered while the section is closed: no pointing at an absent id.
          aria-controls={open ? id : undefined}
          onClick={() => {
            writeOpen(storageKey, !open)
            setOpen(!open)
          }}
          className="flex w-full items-center gap-1 text-left"
        >
          {open ? (
            <ChevronDown className="size-4" aria-hidden />
          ) : (
            <ChevronRight className="size-4" aria-hidden />
          )}
          {title}
        </button>
      </h2>
      {open ? (
        <dl id={id} className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 text-sm">
          {children}
        </dl>
      ) : null}
    </section>
  )
}

/** One editable property: click, type, Enter/blur saves (autosave indicator, IA-13). */
export function InlineProperty({
  label,
  value,
  onSave,
  type,
  required,
  maxLength,
}: {
  label: string
  value: string | null
  onSave: (value: string | null) => Promise<void>
  type?: InlineTextType
  required?: boolean
  maxLength?: number
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">
        <InlineText
          label={label}
          value={value}
          onSave={onSave}
          type={type}
          required={required}
          maxLength={maxLength}
        />
      </dd>
    </>
  )
}

/** A property with custom content (a picker, a read-only value). */
export function StaticProperty({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  )
}
